import { useEffect, useRef, useState, type ComponentType } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Item = {
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  title: string;
  image?: string;
};

export function AudienceCarousel({ items }: { items: Item[] }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [perView, setPerView] = useState(3);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setPerView(w >= 1024 ? 3 : w >= 640 ? 2 : 1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const maxIndex = Math.max(0, items.length - perView);

  const scrollTo = (i: number) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.min(maxIndex, Math.max(0, i));
    const card = el.children[next] as HTMLElement | undefined;
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
    setIndex(next);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      scrollTo(index >= maxIndex ? 0 : index + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, [index, maxIndex, perView]);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const children = Array.from(el.children) as HTMLElement[];
    const closest = children.reduce(
      (best, child, i) =>
        Math.abs(child.offsetLeft - el.offsetLeft - el.scrollLeft) < best.d
          ? { d: Math.abs(child.offsetLeft - el.offsetLeft - el.scrollLeft), i }
          : best,
      { d: Infinity, i: 0 },
    );
    setIndex(closest.i);
  };

  return (
    <div className="group relative mt-10">
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] lg:px-8 [&::-webkit-scrollbar]:hidden"
      >
        {items.map(({ icon: Icon, title, image }) => (
          <li
            key={title}
            className="relative flex w-[62%] shrink-0 snap-start flex-col justify-between overflow-hidden rounded-sm border border-border bg-card p-6 sm:w-[38%] lg:aspect-square lg:w-[calc((100%-2rem)/3)]"
          >
            {image ? (
              <>
                <img
                  src={image}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-card via-card/70 to-card/20" />
              </>
            ) : null}
            <Icon className="relative h-5 w-5 text-nook-green" aria-hidden={true} />
            <h3 className="relative mt-6 text-base font-semibold leading-tight">{title}</h3>
          </li>
        ))}
      </div>

      <button
        type="button"
        aria-label="Perfil anterior"
        onClick={() => scrollTo(index - 1)}
        disabled={index === 0}
        className="absolute left-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground opacity-0 transition-opacity hover:text-nook-green disabled:pointer-events-none disabled:opacity-0 group-hover:opacity-100 lg:flex"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        aria-label="Próximo perfil"
        onClick={() => scrollTo(index + 1)}
        disabled={index >= maxIndex}
        className="absolute right-2 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 text-foreground opacity-0 transition-opacity hover:text-nook-green disabled:pointer-events-none disabled:opacity-0 group-hover:opacity-100 lg:flex"
      >
        <ChevronRight className="h-4 w-4" />
      </button>

      <div className="mt-1 flex items-center justify-center gap-1 lg:mt-0">
        {Array.from({ length: maxIndex + 1 }).map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Ir para slide ${i + 1}`}
            onClick={() => scrollTo(i)}
            className="flex h-9 w-6 items-center justify-center"
          >
            <span
              aria-hidden="true"
              className={`block h-1.5 rounded-full transition-all ${
                i === index ? "w-6 bg-nook-green" : "w-1.5 bg-border"
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
}
